import { motion } from "motion/react";
import {
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  MapPin,
  Mail,
  Phone,
  ArrowUpRight,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";

export function Footer() {
  const currentYear = new Date().getFullYear();

  const navigation = {
    indeMenu: [
      { name: "How It Works", href: "/how-it-works" },
      { name: "Restaurants", href: "/vendors" },
      { name: "Book a Table", href: "/dashboards/customer/bookings" },
      { name: "Order Now", href: "/vendors" },
    ],
    company: [
      { name: "About Us", href: "/pages/about" },
      { name: "Careers", href: "/careers" },
      { name: "Press", href: "/press" },
      { name: "Blog", href: "/blog" },
    ],
    support: [
      { name: "Help Center", href: "/help" },
      { name: "Contact Us", href: "/pages/contact" },
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Service", href: "/terms" },
    ],
  };

  const social = [
    {
      name: "Facebook",
      href: "https://web.facebook.com/profile.php?id=61574456534766/",
      icon: Facebook,
      color: "#1877F2",
    },
    {
      name: "YouTube",
      href: "https://www.youtube.com/channel/UCa4SfBtM4dELcrlVc0Gy4SA",
      icon: Youtube,
      color: "#FF0000",
    },
    {
      name: "Instagram",
      href: "https://www.instagram.com/simpsoft_tech?utm_source=qr&igsh=MXdsMmdjcHk2a3pzbA==",
      icon: Instagram,
      color: "#E1306C",
    },
    {
      name: "LinkedIn",
      href: "https://www.linkedin.com/company/simpsoft-tech/",
      icon: Linkedin,
      color: "#0A66C2",
    },
  ];

  const contactInfo = [
    { icon: MapPin, text: "Lilongwe, Malawi" },
    { icon: Phone, text: "+265 990 024 684" },
    { icon: Mail, text: "info@indemenu.mw" },
  ];

  return (
    <footer className="bg-[#1e3242] text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#FF8C42]/30 to-transparent"></div>
      <div className="absolute top-10 left-10 w-96 h-96 rounded-full bg-[#FF8C42]/5 blur-3xl -z-0"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-[#FFE156]/5 blur-3xl -z-0"></div>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-12">
          {/* Brand Info */}
          <div className="lg:col-span-4 space-y-6">
            <Link href="/" className="inline-flex items-center gap-2 group">
              <div className="relative h-10 w-10 overflow-hidden">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FF8C42] to-[#FFE156] opacity-20 transform transition-transform duration-300 group-hover:scale-100"></div>
                <Image
                  src="/logo.png"
                  alt="Inde Menu Logo"
                  fill
                  sizes="40px"
                  className="object-contain transition-transform duration-300 group-hover:scale-110 relative z-10"
                />
              </div> 
            </Link>

            <p className="text-sm text-white/70 leading-relaxed max-w-md">
              Revolutionizing how Malawians dine — from digital menus to
              seamless ordering and real-time service tracking. Experience the
              future of dining today.
            </p>

            <div className="space-y-3 pt-2">
              {contactInfo.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 text-sm text-white/70"
                >
                  <div className="w-7 h-7 rounded-sm bg-white/10 border border-white/5 flex items-center justify-center">
                    <item.icon className="h-3.5 w-3.5 text-[#FFE156]" />
                  </div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>

            <div className="flex space-x-3 pt-2">
              {social.map((item) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-sm bg-white/10 border border-white/5 flex items-center justify-center hover:bg-white/20 transition-all duration-200"
                  aria-label={item.name}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <item.icon
                    className="h-4 w-4"
                    style={{ color: item.color }}
                  />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Navigation Links */}
          <div className="lg:col-span-8 lg:ml-auto grid grid-cols-1 sm:grid-cols-3 gap-10">
            {/* Inde Menu Links */}
            <div>
              <h3 className="text-[#FFE156] font-medium mb-5 pb-2 border-b border-white/10 flex items-center">
                Inde Menu
              </h3>
              <ul className="space-y-3">
                {navigation.indeMenu.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-sm text-white/80 hover:text-[#FFE156] transition-colors flex items-center gap-1.5 group"
                    >
                      <span>{item.name}</span>
                      <ArrowUpRight className="h-3 w-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Info */}
            <div>
              <h3 className="text-[#FFE156] font-medium mb-5 pb-2 border-b border-white/10 flex items-center">
                Company
              </h3>
              <ul className="space-y-3">
                {navigation.company.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-sm text-white/80 hover:text-[#FFE156] transition-colors flex items-center gap-1.5 group"
                    >
                      <span>{item.name}</span>
                      <ArrowUpRight className="h-3 w-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support Info */}
            <div>
              <h3 className="text-[#FFE156] font-medium mb-5 pb-2 border-b border-white/10 flex items-center">
                Support
              </h3>
              <ul className="space-y-3">
                {navigation.support.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-sm text-white/80 hover:text-[#FFE156] transition-colors flex items-center gap-1.5 group"
                    >
                      <span>{item.name}</span>
                      <ArrowUpRight className="h-3 w-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {/* Footer Bottom */}
        <div className="mt-16 border-t border-white/10 pt-8 text-sm text-white/50 flex flex-col md:flex-row justify-between items-center">
          <p>© {currentYear} Inde Menu Malawi. All rights reserved.</p>
          <div className="mt-4 md:mt-0 flex gap-6">
            <Link
              href="/privacy"
              className="hover:text-white transition-colors"
            >
              Privacy Policy
            </Link>
            <Link href="/terms" className="hover:text-white transition-colors">
              Terms
            </Link>
            <Link
              href="/cookies"
              className="hover:text-white transition-colors"
            >
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
